// src/contexts/DepartmentContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const DepartmentContext = createContext();

export function useDepartment() {
  return useContext(DepartmentContext);
}

export function DepartmentProvider({ children }) {
  const { currentUser } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!currentUser) {
      setDepartments([]);
      setLoading(false);
      return;
    }

    const fetchDepartments = async () => {
      setLoading(true);
      try {
        const companyId = currentUser.uid;
        const departmentsRef = collection(db, 'companies', companyId, 'departments');
        const snapshot = await getDocs(departmentsRef);
        
        const departmentsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setDepartments(departmentsList);
        setError('');
      } catch (err) {
        console.error('Error fetching departments:', err);
        setError('Failed to load departments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, [currentUser]);

  // Add a new department
  const addDepartment = async (name) => {
    if (!currentUser || !name.trim()) return;
    
    try {
      const companyId = currentUser.uid;
      const departmentsRef = collection(db, 'companies', companyId, 'departments');

      // Default opening time (9 AM)
      const openingTime = new Date();
      openingTime.setHours(9, 0, 0, 0);
      
      // Default closing time (5 PM)
      const closingTime = new Date();
      closingTime.setHours(17, 0, 0, 0);
      
      const newDepartment = {
        name: name.trim(),
        openingTime,
        closingTime,
        staffIds: [],
        location: { lat: 0, lon: 0 },
        deviceIds: [],
        // NEW: Default pre-clock settings
        preClockInEnabled: false,
        preClockInMinutes: 10,
        autoClockOutEnabled: false
      };
      
      const docRef = await addDoc(departmentsRef, newDepartment);
      
      // Update local state
      setDepartments(prev => [...prev, { id: docRef.id, ...newDepartment }]);
      return docRef.id;
    } catch (err) {
      console.error('Error adding department:', err);
      setError('Failed to add department. Please try again.');
      return null;
    }
  };

  // Delete a department
  const deleteDepartment = async (departmentId) => {
    if (!currentUser || !departmentId) return;
    
    try {
      const companyId = currentUser.uid;
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      
      // Get department data to find staff members
      const departmentSnap = await getDoc(departmentRef);
      if (!departmentSnap.exists()) {
        throw new Error('Department not found');
      }
      
      const departmentData = departmentSnap.data();
      const staffIds = departmentData.staffIds || [];
      
      // Remove department from all staff members
      for (const staffId of staffIds) {
        const staffRef = doc(db, 'companies', companyId, 'staff', staffId);
        const staffSnap = await getDoc(staffRef);
        
        if (staffSnap.exists()) {
          const staffData = staffSnap.data();
          const departmentIds = staffData.departmentIds || [];
          
          // Remove this department ID
          const updatedDepartmentIds = departmentIds.filter(id => id !== departmentId);
          
          const updateData = { departmentIds: updatedDepartmentIds };
          
          // Update primaryDepartmentId if needed
          if (staffData.primaryDepartmentId === departmentId) {
            updateData.primaryDepartmentId = updatedDepartmentIds[0] || '';
          }
          
          await updateDoc(staffRef, updateData);
        }
      }
      
      // Finally delete the department
      await deleteDoc(departmentRef);
      
      // Update local state
      setDepartments(prev => prev.filter(dept => dept.id !== departmentId));
      
      return true;
    } catch (err) {
      console.error('Error deleting department:', err);
      setError('Failed to delete department. Please try again.');
      return false;
    }
  };

  // Update department name
  const updateDepartmentName = async (departmentId, newName) => {
    if (!currentUser || !departmentId || !newName.trim()) return;
    
    try {
      const companyId = currentUser.uid;
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      
      await updateDoc(departmentRef, { name: newName.trim() });
      
      // Update local state
      setDepartments(prev => prev.map(dept => 
        dept.id === departmentId ? { ...dept, name: newName.trim() } : dept
      ));
      
      return true;
    } catch (err) {
      console.error('Error updating department name:', err);
      setError('Failed to update department name. Please try again.');
      return false;
    }
  };

  // UPDATED: Update department hours with pre-clock settings
  const updateDepartmentHours = async (
    departmentId, 
    openingTime, 
    closingTime, 
    autoClockOutEnabled,
    preClockInEnabled = false,    // NEW parameter
    preClockInMinutes = 10        // NEW parameter
  ) => {
    if (!currentUser || !departmentId) return;
    
    try {
      const companyId = currentUser.uid;
      const departmentRef = doc(db, 'companies', companyId, 'departments', departmentId);
      
      const updateData = { 
        openingTime, 
        closingTime,
        autoClockOutEnabled,
        preClockInEnabled,    // NEW field
        preClockInMinutes     // NEW field
      };
      
      await updateDoc(departmentRef, updateData);
      
      // Update local state
      setDepartments(prev => prev.map(dept => 
        dept.id === departmentId ? { 
          ...dept, 
          openingTime, 
          closingTime,
          autoClockOutEnabled,
          preClockInEnabled,    // NEW field
          preClockInMinutes     // NEW field
        } : dept
      ));
      
      return true;
    } catch (err) {
      console.error('Error updating department hours:', err);
      setError('Failed to update department hours. Please try again.');
      return false;
    }
  };

  const value = {
    departments,
    loading,
    error,
    addDepartment,
    deleteDepartment,
    updateDepartmentName,
    updateDepartmentHours
  };

  return (
    <DepartmentContext.Provider value={value}>
      {children}
    </DepartmentContext.Provider>
  );
}